import React, { useState, useEffect } from 'react';
import { Modal, Input, Radio, Space, Typography, Alert, message as antdMessage, Tag } from 'antd';
import { WhatsAppOutlined } from '@ant-design/icons';
import { whatsappApi } from '../../utils/api';
import { appliquerVariables } from '../../utils/messageTemplate';
import WhatsAppProviderBadge from '../whatsapp/WhatsAppProviderBadge';

const { Text, Paragraph } = Typography;
const { TextArea } = Input;

/**
 * Modal générique d'envoi de message WhatsApp.
 *
 * Composant réutilisable pour n'importe quel destinataire (client, livreur, fournisseur...)
 * et n'importe quelle liste de messages prédéfinis. Les templates reçus sont
 * personnalisés avec les variables fournies, puis le message reste éditable
 * avant l'envoi.
 *
 * @param {boolean} visible - Affichage du modal
 * @param {Function} onCancel - Callback de fermeture
 * @param {Function} [onSent] - Callback appelé après un envoi réussi (reçoit le message envoyé)
 * @param {string} phone - Numéro de téléphone du destinataire
 * @param {string} [destinataireNom] - Nom affiché du destinataire
 * @param {Array} [templates] - Messages prédéfinis [{ cle, libelle, resume, contenu }]
 * @param {Object} [variables] - Dictionnaire de valeurs pour les variables {{cle}} des templates
 * @param {Array} [variablesDisponibles] - Aide affichée [{ cle, description }]
 * @param {string} [title] - Titre du modal
 */
const SendWhatsAppMessageModal = ({
  visible,
  onCancel,
  onSent,
  phone,
  destinataireNom,
  templates = [],
  variables = {},
  variablesDisponibles = [],
  title = 'Envoyer un message WhatsApp'
}) => {
  // Template actuellement sélectionné (null = message libre)
  const [templateSelectionne, setTemplateSelectionne] = useState(null);
  // Contenu final du message, éditable par l'utilisateur
  const [contenuMessage, setContenuMessage] = useState('');
  // Indicateur d'envoi en cours
  const [envoiEnCours, setEnvoiEnCours] = useState(false);

  /**
   * À l'ouverture du modal, présélectionne le premier template disponible
   * et réinitialise l'état pour éviter de conserver un message précédent.
   */
  useEffect(() => {
    if (visible) {
      const premierTemplate = templates.length > 0 ? templates[0] : null;
      setTemplateSelectionne(premierTemplate ? premierTemplate.cle : null);
      setContenuMessage(
        premierTemplate ? appliquerVariables(premierTemplate.contenu, variables) : ''
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  /**
   * Applique le template choisi : le contenu éditable est remplacé
   * par le template personnalisé avec les variables de la commande.
   */
  const handleSelectionTemplate = (cle) => {
    setTemplateSelectionne(cle);
    const template = templates.find((t) => t.cle === cle);
    setContenuMessage(template ? appliquerVariables(template.contenu, variables) : '');
  };

  /**
   * Envoie le message au destinataire via l'API WhatsApp existante.
   */
  const handleEnvoyer = async () => {
    if (!phone) {
      antdMessage.error('Aucun numéro de téléphone disponible pour ce destinataire');
      return;
    }

    if (!contenuMessage.trim()) {
      antdMessage.warning('Le message ne peut pas être vide');
      return;
    }

    setEnvoiEnCours(true);
    try {
      // Dernière substitution : permet à l'utilisateur d'ajouter lui-même
      // des variables {{cle}} dans le texte avant l'envoi
      const messageFinal = appliquerVariables(contenuMessage, variables);

      await whatsappApi.sendMessage({
        phone,
        message: messageFinal
      });

      antdMessage.success('Message WhatsApp envoyé avec succès');

      if (onSent) onSent(messageFinal);
      onCancel();
    } catch (error) {
      console.error('Erreur lors de l\'envoi du message WhatsApp:', error);
      // Message d'erreur explicite : la cause la plus fréquente est la déconnexion de WhatsApp
      antdMessage.error(
        error?.response?.data?.error === 'WhatsApp not connected'
          ? 'WhatsApp n\'est pas connecté. Veuillez le reconnecter dans les paramètres.'
          : 'Échec de l\'envoi du message WhatsApp. Veuillez réessayer.'
      );
    } finally {
      setEnvoiEnCours(false);
    }
  };

  return (
    <Modal
      title={
        <Space>
          <WhatsAppOutlined style={{ color: '#25D366' }} />
          {title}
          <WhatsAppProviderBadge />
        </Space>
      }
      open={visible}
      onCancel={onCancel}
      onOk={handleEnvoyer}
      okText="Envoyer"
      cancelText="Annuler"
      confirmLoading={envoiEnCours}
      okButtonProps={{
        icon: <WhatsAppOutlined />,
        style: { backgroundColor: '#25D366', borderColor: '#25D366' },
        disabled: !phone
      }}
      width={640}
      destroyOnClose
    >
      {/* Rappel du destinataire pour éviter tout envoi au mauvais numéro */}
      {phone ? (
        <Paragraph className="mb-3">
          <Text type="secondary">Destinataire : </Text>
          <Text strong>
            {destinataireNom ? `${destinataireNom} — ` : ''}
            {phone}
          </Text>
        </Paragraph>
      ) : (
        <Alert
          type="warning"
          showIcon
          className="mb-3"
          message="Aucun numéro de téléphone enregistré pour ce destinataire"
        />
      )}

      {/* Sélection d'un message prédéfini */}
      {templates.length > 0 && (
        <div className="mb-3">
          <Text strong className="block mb-2">Message prédéfini</Text>
          <Radio.Group
            value={templateSelectionne}
            onChange={(e) => handleSelectionTemplate(e.target.value)}
            className="w-full"
          >
            <Space direction="vertical" className="w-full">
              {templates.map((template) => (
                <Radio key={template.cle} value={template.cle}>
                  <Text>{template.libelle}</Text>
                  {template.resume && (
                    <Text type="secondary" className="ml-2 text-xs">
                      ({template.resume})
                    </Text>
                  )}
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        </div>
      )}

      {/* Message final, modifiable avant envoi */}
      <Text strong className="block mb-2">Message à envoyer</Text>
      <TextArea
        value={contenuMessage}
        onChange={(e) => setContenuMessage(e.target.value)}
        rows={10}
        placeholder="Saisissez votre message..."
        maxLength={4000}
        showCount
      />

      {/* Aide : variables utilisables si l'utilisateur écrit son propre message */}
      {variablesDisponibles.length > 0 && (
        <div className="mt-3">
          <Text type="secondary" className="text-xs block mb-1">
            Variables disponibles (remplacées à l'envoi si vous les ajoutez) :
          </Text>
          <Space size={[4, 4]} wrap>
            {variablesDisponibles.map((variable) => (
              <Tag key={variable.cle} className="text-xs">
                {`{{${variable.cle}}}`}
              </Tag>
            ))}
          </Space>
        </div>
      )}
    </Modal>
  );
};

export default SendWhatsAppMessageModal;
